'use client'

import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
	Drawer,
	DrawerContent,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger,
} from '@/components/ui/drawer'
import { Input } from '@/components/ui/input'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'
import { format, isValid, parse } from 'date-fns'
import { es } from 'date-fns/locale'
import { CalendarIcon } from 'lucide-react'
import * as React from 'react'

export interface DateInputProps {
	label?: string
	value: Date | undefined
	onChange: (date: Date | undefined) => void
	disabled?: boolean
	disabledDates?: (date: Date) => boolean
	className?: string
	placeholder?: string
}

export function DateInput({
	label,
	value,
	onChange,
	disabled,
	disabledDates,
	className,
	placeholder = 'dd/mm/aaaa',
}: DateInputProps) {
	const [inputValue, setInputValue] = React.useState('')
	const [isPopoverOpen, setIsPopoverOpen] = React.useState(false)
	const isMobile = useIsMobile()

	// Sync input with date prop
	React.useEffect(() => {
		if (value) {
			setInputValue(format(value, 'dd/MM/yyyy'))
		} else {
			setInputValue('')
		}
	}, [value])

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = e.target.value
		setInputValue(newValue)

		if (newValue === '') {
			onChange(undefined)
			return
		}

		const parsedDate = parse(newValue, 'dd/MM/yyyy', new Date())

		if (isValid(parsedDate)) {
			// Check if date is disabled
			if (disabledDates && disabledDates(parsedDate)) {
				return
			}
			onChange(parsedDate)
		}
	}

	const handleSelect = (date: Date | undefined) => {
		onChange(date)
		setIsPopoverOpen(false)
	}

	const trigger = (
		<Button
			variant='ghost'
			size='icon'
			className='absolute right-0 top-0 h-full px-3 hover:bg-transparent text-muted-foreground'
			disabled={disabled}
		>
			<CalendarIcon className='h-4 w-4' />
		</Button>
	)

	const calendar = (
		<Calendar
			mode='single'
			selected={value}
			onSelect={handleSelect}
			disabled={disabledDates}
			autoFocus={!isMobile}
			locale={es}
		/>
	)

	return (
		<div className={cn('space-y-2', className)}>
			{label && (
				<label className='text-sm font-medium text-[#09194D]'>{label}</label>
			)}
			<div className='relative'>
				<Input
					type='text'
					placeholder={placeholder}
					value={inputValue}
					onChange={handleInputChange}
					disabled={disabled}
					className={cn('pr-10 cursor-text', !value && 'text-muted-foreground')}
				/>
				{isMobile ? (
					<Drawer open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
						<DrawerTrigger asChild>{trigger}</DrawerTrigger>
						<DrawerContent>
							<DrawerHeader className='sr-only'>
								<DrawerTitle>Seleccionar fecha</DrawerTitle>
							</DrawerHeader>
							<div className='mx-auto overflow-auto pb-6'>{calendar}</div>
						</DrawerContent>
					</Drawer>
				) : (
					<Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
						<PopoverTrigger asChild>{trigger}</PopoverTrigger>
						<PopoverContent
							className='w-auto p-0'
							align='start'
							collisionPadding={8}
						>
							{calendar}
						</PopoverContent>
					</Popover>
				)}
			</div>
		</div>
	)
}
